import { useMemo } from "react";

import { WebhooksLogoIcon } from "@phosphor-icons/react";

import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import GridWrapper, { GridAlignContent, GridAlignItems } from "@gx/dls/containers/GridWrapper";
import { SourceSyncOperationMetadataWebhook } from "@gx/ts-contracts/services/source/resources/source_sync_operation_metadata/source_sync_operation_metadata_pb";

import EmptyLayout from "@/layouts/EmptyLayout";

import { formatFileSize } from "@/pages/sources/components/dropzone/utils";
import EditSourceContentToolbar from "@/pages/sources/components/edit/tabs/content/EditSourceContentToolbar";
import SourceContentFileBadge from "@/pages/sources/components/SourceContentFileBadge";

interface EditSourceContentWebhookProps {
  items: SourceSyncOperationMetadataWebhook[];
  search: string;
  onSearchChange: (value: string) => void;
}

const EditSourceContentWebhook = ({
  items,
  search,
  onSearchChange,
}: EditSourceContentWebhookProps) => {
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      (item) =>
        item.idempotencyKey?.toLowerCase().includes(query) ||
        item.sourceIp?.toLowerCase().includes(query),
    );
  }, [items, search]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} fillWidth fillHeight minHeight={0}>
      <EditSourceContentToolbar
        search={search}
        count={items.length}
        onSearchChange={onSearchChange}
      />
      <FlexWrapper padding="12px" fillWidth fillHeight minHeight={0} overflow="auto">
        {filtered.length > 0 ? (
          <GridWrapper
            columns="repeat(auto-fill, minmax(240px, 1fr))"
            gap={8}
            alignItems={GridAlignItems.START}
            alignContent={GridAlignContent.START}
            fillWidth
          >
            {filtered.map((item) => (
              <SourceContentFileBadge
                key={item.idempotencyKey}
                header={item.idempotencyKey ?? "—"}
                subHeader={item.payloadSize ? formatFileSize(Number(item.payloadSize)) : null}
                icon={WebhooksLogoIcon}
              />
            ))}
          </GridWrapper>
        ) : (
          <EmptyLayout title="No content" description="No content matches your search" />
        )}
      </FlexWrapper>
    </FlexWrapper>
  );
};

export default EditSourceContentWebhook;
